import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, useNavigate } from "react-router-dom";
import {
  HiOutlineCheckCircle,
  HiOutlineClipboardCheck,
  HiOutlineDocumentText,
  HiOutlineEye,
  HiOutlineFlag,
  HiOutlineLightBulb,
  HiOutlineUserGroup,
} from "react-icons/hi";
import characterFormELBB from "assets/img/2026/characterFormELBB.png";
import { HeaderProduto } from "components/HeaderProduto";
import { FormField } from "components/ui/FormField";
import { FormHeader } from "components/ui/FormHeader";
import { FormNavigation } from "components/ui/FormNavigation";
import { FormStep } from "components/ui/FormStep";
import { FormTextArea } from "components/ui/FormTextArea";
import { ResumoPreInscricao } from "components/ui/ResumoPreInscricao";
import { SelectField } from "components/ui/SelectField";
import { StarDecorations } from "components/ui/StarDecorations";
import { StepIndicator } from "components/ui/StepIndicator";
import { SuccessCardELBB } from "components/ui/SuccessCardELBB";
import { CONFIG_PRE_INSCRICAO, DESCRICAO_UNIDADE_VALIDADORA, ROTULO_UNIDADE_VALIDADORA } from "config/pre-inscricao";
import { PRODUTO_CONFIG } from "config/produto";
import { getProduto } from "config/produto-env";
import { useAuth } from "context/AuthContext";
import { useControlDate } from "hooks/useControlDate";
import {
  CAMPOS_POR_ETAPA,
  LIMITE_TEXTO,
  LIMITE_TITULO,
  PreInscricaoForm,
  preInscricaoSchema,
  validarRegrasDaCategoria,
} from "schemas/pre-inscricao.schema";
import { atualizarIniciativa, salvarIniciativa } from "services/iniciativa-elbb.service";
import {
  buscarDirecionadores,
  buscarEntregasPreInscricao,
  buscarUnidadesValidadoras,
  invalidarEntregasPreInscricao,
  PessoaParticipante,
} from "services/pre-inscricao.service";
import { isModoSimulacao, MENSAGEM_MODO_SIMULACAO } from "utils/modo-simulacao";
import { AJUDA_DOS_CAMPOS, CATEGORIAS_CONEXAO, CATEGORIAS_ECBB, getNomeCategoriaConexao, perguntaNorteadora, SEMESTRES_2026 } from "utils/direcionadores";
import { CategoriaPreInscricao, filtrarUnidadesPorTipo, getCategoriasPermitidas, getNomeExibicao, getUnidadeSubordinante } from "utils/pessoa";
import { formDaIniciativa, IniciativaPreInscricao, montarPayloadPreInscricao } from "utils/pre-inscricao-payload";
import { BuscaParticipantes } from "./components/BuscaParticipantes";
import { DirecionadorCards } from "./components/DirecionadorCards";
import { UnidadeSelector } from "./components/UnidadeSelector";
import { useListaRemota } from "./useListaRemota";

/** `resumo` é a conferência dos dados antes do envio; não tem campos próprios. */
type Etapa = keyof typeof CAMPOS_POR_ETAPA | "resumo";

const ETAPAS_DO_FORMULARIO: Etapa[] = ["informacoes", "direcionador", "participantes", "descricao", "resumo"];

const ICONES: Record<Etapa, ReactElement> = {
  categoria: <HiOutlineFlag className="text-pink-bb" />,
  informacoes: <HiOutlineDocumentText className="text-pink-bb" />,
  direcionador: <HiOutlineLightBulb className="text-pink-bb" />,
  participantes: <HiOutlineUserGroup className="text-pink-bb" />,
  descricao: <HiOutlineClipboardCheck className="text-pink-bb" />,
  resumo: <HiOutlineEye className="text-pink-bb" />,
};

const formatarData = (valor?: Date | string | null) =>
  valor ? new Date(valor).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }) : null;

/** Aviso da pré-inscrição fechada: diz se ela ainda vai abrir ou se já terminou. */
/**
 * Pré-inscrição 2027: formulário único para Negócios e Atendimento, Pilar de
 * Sustentação e ELBB. A categoria decide a configuração (CONFIG_PRE_INSCRICAO).
 */
export function PreInscricao() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const produto = getProduto();
  const periodo = useControlDate(
    produto === "PREMIO_ELBB" ? PRODUTO_CONFIG.phaseIds.ELBB_PRE_REGISTRATION : PRODUTO_CONFIG.phaseIds.CONEXAO_PRE_REGISTRATION
  );
  const pessoa = user?.pessoa ?? null;

  const iniciativaEditada: IniciativaPreInscricao | undefined = (location.state as any)?.editData;
  const isEdicao = Boolean(iniciativaEditada?._id);
  const categoriasPermitidas = useMemo(() => getCategoriasPermitidas(pessoa, produto), [pessoa, produto]);
  const escolheCategoria = !isEdicao && categoriasPermitidas.length > 1;
  const categoriaInicial = categoriasPermitidas.length === 1 ? categoriasPermitidas[0] : undefined;

  const [indiceEtapa, setIndiceEtapa] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [concluido, setConcluido] = useState(false);
  const [jaInscrito, setJaInscrito] = useState(false);
  const unidades = useListaRemota(buscarUnidadesValidadoras, "unidades");
  const direcionadores = useListaRemota(buscarDirecionadores, "direcionadores");
  const formRef = useRef<HTMLFormElement>(null);

  const valoresIniciais = useMemo<PreInscricaoForm>(
    () =>
      iniciativaEditada
        ? formDaIniciativa(iniciativaEditada)
        : {
            categoria: categoriaInicial as CategoriaPreInscricao,
            titulo: "",
            semestre: undefined as unknown as "1",
            unidade: getUnidadeSubordinante(pessoa, categoriaInicial),
            categoriaEcbb: "",
            categoriaConexao: "",
            direcionadorId: "",
            participantes: [],
            descricao: "",
            resultado: "",
          },
    // Valores iniciais só no primeiro render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { register, control, watch, setValue, trigger, setError, clearErrors, getValues, formState: { errors } } =
    useForm<PreInscricaoForm>({ resolver: zodResolver(preInscricaoSchema), defaultValues: valoresIniciais });

  const form = watch();

  // Os links do menu recarregam a página: avisa antes de perder o que foi preenchido.
  const temAlteracoes = !concluido && JSON.stringify(form) !== JSON.stringify(valoresIniciais);
  useEffect(() => {
    if (!temAlteracoes) return;
    const avisarSaida = (evento: BeforeUnloadEvent) => evento.preventDefault();
    window.addEventListener("beforeunload", avisarSaida);
    return () => window.removeEventListener("beforeunload", avisarSaida);
  }, [temAlteracoes]);
  const config = form.categoria ? CONFIG_PRE_INSCRICAO[form.categoria] : null;
  const etapas: Etapa[] = [
    ...(escolheCategoria ? (["categoria"] as const) : []),
    ...ETAPAS_DO_FORMULARIO,
  ];
  const etapaAtual = etapas[indiceEtapa];
  const direcionador = direcionadores.itens.find((d) => d._id === form.direcionadorId);

  // Limite de 1 iniciativa por pessoa: o trigger que fazia isso não roda na coleção temporária.
  useEffect(() => {
    if (isEdicao || !pessoa) return;
    buscarEntregasPreInscricao()
      .then((iniciativas) => setJaInscrito(iniciativas.some((i: any) => i.owner === pessoa._id)))
      .catch(() => setJaInscrito(false));
  }, [isEdicao, pessoa]);

  useEffect(() => {
    setTimeout(() => {
      const secao = formRef.current?.querySelector('section[role="region"]');
      secao?.querySelector<HTMLElement>("input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role=radio][tabindex='0']")?.focus();
    }, 50);
  }, [indiceEtapa]);

  const validarEtapa = async (etapa: Etapa) => {
    if (etapa === "resumo") return true;
    const campos = CAMPOS_POR_ETAPA[etapa];
    const camposValidos = await trigger(campos);
    const errosCategoria = validarRegrasDaCategoria(getValues()).filter((erro) => campos.includes(erro.campo));
    errosCategoria.forEach((erro) => setError(erro.campo, { type: "custom", message: erro.mensagem }));
    return camposValidos && errosCategoria.length === 0;
  };

  const avancar = async () => {
    if (await validarEtapa(etapaAtual)) setIndiceEtapa((i) => Math.min(i + 1, etapas.length - 1));
  };

  const voltar = () => {
    // Na primeira etapa o botão só aparece na edição, e volta para a listagem.
    if (indiceEtapa === 0) navigate("/listagem");
    else setIndiceEtapa((i) => i - 1);
  };

  const enviar = async () => {
    if (isSubmitting || !pessoa) return;
    if (!direcionador) {
      setError("direcionadorId", { type: "custom", message: "Escolha o direcionador transversal" });
      setIndiceEtapa(etapas.indexOf("direcionador"));
      return;
    }
    for (const etapa of etapas) {
      if (!(await validarEtapa(etapa))) {
        setIndiceEtapa(etapas.indexOf(etapa));
        return;
      }
    }
    setIsSubmitting(true);
    setErroEnvio(null);
    try {
      // O direcionador vem da lista carregada; a validação da etapa garante que há um escolhido.
      const payload = montarPayloadPreInscricao(getValues(), pessoa, direcionador!, iniciativaEditada);
      if (isEdicao) await atualizarIniciativa(payload);
      else await salvarIniciativa(payload);
      invalidarEntregasPreInscricao();
      setConcluido(true);
    } catch (error) {
      console.error("[PreInscricao] falha ao enviar", error);
      setErroEnvio(
        isModoSimulacao()
          ? MENSAGEM_MODO_SIMULACAO
          : "Não foi possível enviar a pré-inscrição. Seus dados foram mantidos; tente novamente."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const adicionarParticipante = (participante: PessoaParticipante) => {
    setValue("participantes", [...form.participantes, { _id: participante._id, nome: participante.nome_guerra || participante.nome }]);
    clearErrors("participantes");
  };

  const removerParticipante = (id: string) =>
    setValue("participantes", form.participantes.filter((p) => p._id !== id));

  if (concluido && config) {
    return (
      <>
        <HeaderProduto currentPage="pre-inscricao" />
        <SuccessCardELBB
          onClose={() => navigate("/listagem")}
          type={config.categoria}
          categoriaConexao={getNomeCategoriaConexao(form.categoriaConexao)}
        />
      </>
    );
  }

  const aviso = !pessoa
    ? "Seus dados ainda não constam na base do Prêmio BB 2027."
    : jaInscrito
      ? "Você já tem uma iniciativa pré-inscrita. Acompanhe ou edite pela sua listagem."
      : null;

  const totalParticipantes = form.participantes.length + 1;
  const noLimite = config ? totalParticipantes >= config.participantes.max : true;
  const dataFim = formatarData(periodo?.dataFim);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-bb via-blue-bb-1 to-blue-bb-5 overflow-x-hidden relative">
      <HeaderProduto currentPage="pre-inscricao" />

      <div aria-hidden="true" className="hidden md:block pointer-events-none">
        <StarDecorations count={18} density="medium" includeEmojis={true} includeCrosses={false} />
      </div>

      <div className="flex flex-col items-start container px-4 sm:px-6 my-4 sm:my-6 lg:my-8 mx-auto">
        <h1 className="text-green-bb-2 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-5 lg:mb-6">
          {isEdicao ? "Editar pré-inscrição" : "Pré-inscrição de iniciativa"}
        </h1>

        <div className="bg-white-bb rounded-xl sm:rounded-2xl p-4 sm:p-6 md:px-8 lg:px-12 xl:px-20 py-6 sm:py-8 w-full shadow-xl sm:shadow-2xl relative">
          {aviso ? (
            <div className="text-center py-10 space-y-4" role="status">
              <p className="text-lg text-gray-bb-1 font-medium">{aviso}</p>
              <button
                type="button"
                onClick={() => navigate(jaInscrito ? "/listagem" : produto === "PREMIO_ELBB" ? "/home-elbb" : "/home-public")}
                className="px-6 py-3 rounded-full bg-yellow-bb text-blue-bb-3 font-bold"
              >
                {jaInscrito ? "Ver minha iniciativa" : "Ir para a página inicial"}
              </button>
            </div>
          ) : (
            <>
              <StepIndicator
                currentStep={indiceEtapa + 1}
                steps={etapas.map((etapa, i) => ({
                  number: i + 1,
                  activeColor: "bg-pink-bb",
                  icon: i === etapas.length - 1 && indiceEtapa === i ? <HiOutlineCheckCircle className="text-green-bb-2" /> : ICONES[etapa],
                }))}
                progressColor="bg-pink-bb"
              />

              <form ref={formRef} className="mt-6 sm:mt-8" aria-label="Formulário de pré-inscrição" onSubmit={(e) => e.preventDefault()}>
                {escolheCategoria && (
                  <FormStep stepNumber={1} isActive={etapaAtual === "categoria"}>
                    <h2 className="text-xl font-bold text-gray-bb-1 mb-2">Em qual categoria deseja pré-inscrever?</h2>
                    <p className="text-gray-bb-2 mb-5">Você participa dos dois programas. Escolha a categoria desta iniciativa.</p>
                    <div role="radiogroup" aria-label="Categoria" className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {categoriasPermitidas.map((categoria) => (
                        <button
                          key={categoria}
                          type="button"
                          role="radio"
                          aria-checked={form.categoria === categoria}
                          onClick={() => {
                            if (categoria !== form.categoria) setValue("unidade", getUnidadeSubordinante(pessoa, categoria));
                            setValue("categoria", categoria);
                            clearErrors("categoria");
                          }}
                          className={`text-left rounded-xl border-2 p-4 font-bold text-blue-bb transition-all ${
                            form.categoria === categoria ? "border-blue-bb bg-blue-50" : "border-gray-bb-4 hover:border-blue-bb-5"
                          }`}
                        >
                          {CONFIG_PRE_INSCRICAO[categoria].rotuloEscolha}
                        </button>
                      ))}
                    </div>
                    {errors.categoria && <p className="text-red-500 text-sm mt-2">{errors.categoria.message}</p>}
                  </FormStep>
                )}

                {config && (
                  <>
                    <FormStep stepNumber={etapas.indexOf("informacoes") + 1} isActive={etapaAtual === "informacoes"}>
                      <FormHeader
                        title={`Faça a pré-inscrição da ${config.tituloIniciativa} no`}
                        subtitle={config.subtitulo}
                        stepTitle="Informações da iniciativa:"
                        characterImage={characterFormELBB}
                      />
                      {dataFim && (
                        <p className="mb-4 text-sm text-blue-bb bg-blue-50 border border-blue-100 rounded-lg p-3">
                          Você pode editar a iniciativa até o fim da pré-inscrição, em <strong>{dataFim}</strong>.
                        </p>
                      )}
                      <div className="space-y-3 sm:space-y-4">
                        <FormField
                          label="Nome da iniciativa"
                          type="text"
                          placeholder="Digite o nome da iniciativa"
                          maxLength={LIMITE_TITULO}
                          showCounter
                          value={form.titulo}
                          error={errors.titulo}
                          register={register("titulo")}
                          tooltip={AJUDA_DOS_CAMPOS.nome}
                        />
                        {config.temCategoriaConexao && (
                          <SelectField
                            label="Em qual categoria deseja pré-inscrever?"
                            options={CATEGORIAS_CONEXAO.map((c) => ({ value: c.id, label: c.nome }))}
                            placeholder="Selecione a categoria"
                            error={errors.categoriaConexao}
                            register={register("categoriaConexao")}
                          />
                        )}
                        <SelectField
                          label="Em qual semestre?"
                          options={SEMESTRES_2026.map((s) => ({ value: s.valor, label: s.rotulo }))}
                          placeholder="Selecione o semestre"
                          error={errors.semestre}
                          register={register("semestre")}
                          tooltip={AJUDA_DOS_CAMPOS.semestre}
                        />
                        <Controller
                          control={control}
                          name="unidade"
                          render={({ field }) => (
                            <UnidadeSelector
                              rotulo={ROTULO_UNIDADE_VALIDADORA}
                              descricao={DESCRICAO_UNIDADE_VALIDADORA}
                              tooltip={AJUDA_DOS_CAMPOS.unidade}
                              unidades={filtrarUnidadesPorTipo(unidades.itens, config.tipoUnidade)}
                              carregando={unidades.carregando}
                              falhou={unidades.falhou}
                              onTentarNovamente={unidades.recarregar}
                              value={field.value}
                              onChange={field.onChange}
                              error={errors.unidade?.message}
                            />
                          )}
                        />
                        {config.temCategoriaEcbb && (
                          <SelectField
                            label="Categoria ECBB"
                            options={CATEGORIAS_ECBB.map((c) => ({ value: c.id, label: c.nome }))}
                            placeholder="Selecione a categoria ECBB"
                            error={errors.categoriaEcbb}
                            register={register("categoriaEcbb")}
                            tooltip={AJUDA_DOS_CAMPOS.categoriaEcbb}
                          />
                        )}
                      </div>
                    </FormStep>

                    <FormStep stepNumber={etapas.indexOf("direcionador") + 1} isActive={etapaAtual === "direcionador"}>
                      <h2 className="text-xl font-bold text-gray-bb-1 mb-2">Direcionador transversal</h2>
                      <p className="text-gray-bb-2 mb-5">{AJUDA_DOS_CAMPOS.direcionador}</p>
                      <DirecionadorCards
                        direcionadores={direcionadores.itens}
                        carregando={direcionadores.carregando}
                        falhou={direcionadores.falhou}
                        onTentarNovamente={direcionadores.recarregar}
                        value={form.direcionadorId}
                        onChange={(id) => {
                          setValue("direcionadorId", id);
                          clearErrors("direcionadorId");
                        }}
                        error={errors.direcionadorId?.message}
                      />
                    </FormStep>

                    <FormStep stepNumber={etapas.indexOf("participantes") + 1} isActive={etapaAtual === "participantes"}>
                      <h2 className="text-xl font-bold text-gray-bb-1 mb-2">Participantes</h2>
                      <p className="text-gray-bb-2 mb-5">
                        {AJUDA_DOS_CAMPOS.participantes} Mínimo de {config.participantes.min} e máximo de{" "}
                        {config.participantes.max} participantes, incluindo você.
                      </p>
                      <BuscaParticipantes
                        programa={config.programaParticipante}
                        ignorar={[pessoa!._id, ...form.participantes.map((p) => p._id)]}
                        desabilitado={noLimite}
                        onAdicionar={adicionarParticipante}
                      />
                      <ul className="mt-5 space-y-2" aria-label="Participantes adicionados">
                        <li className="flex items-center bg-blue-50 border border-blue-200 p-3 rounded-lg">
                          <span className="text-gray-bb-1 font-medium">{getNomeExibicao(pessoa)}</span>
                          <span className="text-sm text-gray-bb-2 ml-2">({pessoa!._id})</span>
                          <span className="text-sm text-blue-bb ml-2">(Você)</span>
                        </li>
                        {form.participantes.map((participante) => (
                          <li key={participante._id} className="flex items-center justify-between bg-gray-bb-3 p-3 rounded-lg">
                            <span>
                              <span className="text-gray-bb-1 font-medium">{participante.nome}</span>
                              <span className="text-sm text-gray-bb-2 ml-2">({participante._id})</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => removerParticipante(participante._id)}
                              aria-label={`Remover ${participante.nome} dos participantes`}
                              className="text-red-500 hover:text-red-700"
                            >
                              ✕
                            </button>
                          </li>
                        ))}
                      </ul>
                      <p className="text-gray-bb-2 mt-4">
                        <strong>Total de participantes:</strong> {totalParticipantes} de {config.participantes.max}
                      </p>
                      {errors.participantes && <p className="text-red-500 text-sm mt-1">{errors.participantes.message}</p>}
                    </FormStep>

                    <FormStep stepNumber={etapas.indexOf("descricao") + 1} isActive={etapaAtual === "descricao"}>
                      <FormTextArea
                        label="Descrição"
                        rows={8}
                        maxLength={LIMITE_TEXTO}
                        showCounter
                        placeholder="Problema, desafio ou oportunidade; solução; principais ações; e diferencial."
                        value={form.descricao}
                        error={errors.descricao}
                        register={register("descricao")}
                        tooltip={AJUDA_DOS_CAMPOS.descricao}
                      />
                      <div className="mt-6">
                        <FormTextArea
                          label="Pergunta obrigatória"
                          description={perguntaNorteadora(direcionador?.nome)}
                          rows={8}
                          maxLength={LIMITE_TEXTO}
                          showCounter
                          placeholder="Descreva os resultados alcançados..."
                          value={form.resultado}
                          error={errors.resultado}
                          register={register("resultado")}
                          tooltip={AJUDA_DOS_CAMPOS.pergunta}
                          complemento={
                            direcionador && (
                              <div className="mb-3 rounded-lg bg-blue-50 border border-blue-100 p-3 text-sm">
                                <span className="font-bold text-blue-bb">{direcionador.nome}: </span>
                                <span className="text-gray-bb-1">{direcionador.descricao}</span>
                              </div>
                            )
                          }
                        />
                      </div>
                    </FormStep>

                    <FormStep stepNumber={etapas.indexOf("resumo") + 1} isActive={etapaAtual === "resumo"}>
                      <h2 className="text-xl font-bold text-gray-bb-1 mb-2">Confira os dados antes de enviar</h2>
                      <p className="text-gray-bb-2 mb-5">
                        Revise as informações da pré-inscrição. Para corrigir algo, use “Editar” na seção correspondente.
                      </p>
                      {direcionador && (
                        <ResumoPreInscricao
                          iniciativa={montarPayloadPreInscricao(getValues(), pessoa!, direcionador, iniciativaEditada)}
                          onEditar={(secao) => setIndiceEtapa(etapas.indexOf(secao))}
                        />
                      )}
                      {erroEnvio && (
                        <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
                          {erroEnvio}
                        </p>
                      )}
                    </FormStep>
                  </>
                )}

                <FormNavigation
                  currentStep={indiceEtapa + 1}
                  totalSteps={etapas.length}
                  onPrev={voltar}
                  onNext={avancar}
                  onSubmit={enviar}
                  isLastStep={indiceEtapa === etapas.length - 1}
                  hideBackButton={indiceEtapa === 0 && !isEdicao}
                  isSubmitting={isSubmitting}
                />
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
